/**
 * The Metals page tab "Stock Summary": what `metal_stock` holds now, per metal, with each fineness
 * summed over the inventories that contributed to it, plus the figures shown above the tables
 * (gold in AU585 and AU999.99, PMP, worth in RON and EUR). The server sends the figures as MySQL
 * decimals, so they may be strings.
 */

import type { StockState } from '../metals';
import { classifyMetal, formatPurityPercent, type IncomingSummaryMetal } from './incomingSummary';

/** The fineness every gold quantity is expressed in as "AU585". */
const AU585_FINENESS = 585;

/** One row of a stock table: a fineness, or one inventory date of it (a child row). */
export interface StockTableRow {
    key: string;
    kind: 'fineness' | 'inventory';
    /** The fineness (`585`) or the inventory date (`YYYY-MM-DD`). */
    label: string;
    /** The fineness as a percentage, e.g. `58,5%`; empty on an inventory row. */
    percent: string;
    quantity: number;
    purifiedQuantity: number;
    /** The PMP, RON per gram. */
    avgPrice: number;
    valueRon: number;
    children?: StockTableRow[];
}

/** The figures of a summary block: one metal, or the whole stock. */
export interface StockFigures {
    quantity: number;
    /** Fine metal: gold to 999.99, everything else to 1000. */
    purifiedQuantity: number;
    /** Gold expressed at fineness 585; `null` for a metal that is not gold. */
    au585Quantity: number | null;
    avgPrice: number;
    valueRon: number;
    /** `null` when no exchange rate is known. */
    valueEur: number | null;
}

export interface StockMetalTotal extends StockFigures {
    metalType: string;
    kind: IncomingSummaryMetal;
    /** Every fineness of the metal, in the order the server listed them. */
    rows: StockTableRow[];
}

/** Grams to 2 decimals, money (RON, EUR) to none, as the rest of the app shows them. */
export const GRAMS_DIGITS = 2;
export const MONEY_DIGITS = 0;

/** A figure with a decimal comma and thousands dots: `1.234,50`. */
export const formatStockNumber = (value: number | string, digits: number): string =>
    Number(value).toLocaleString('ro-RO', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

const toNumber = (value: number | string): number => Number(value) || 0;

/** RON per gram, 0 for no grams. */
const perGram = (valueRon: number, quantity: number): number =>
    quantity > 0 ? valueRon / quantity : 0;

const toEur = (valueRon: number, exchangeRate: number | null): number | null =>
    exchangeRate ? valueRon / exchangeRate : null;

/** The grams of a gold lot at fineness 585: `grams × fineness / 585`. */
const toAu585 = (quantity: number, fineness: number): number =>
    (quantity * fineness) / AU585_FINENESS;

const inventoryRow = (row: StockState): StockTableRow => {
    const quantity = toNumber(row.quantity);
    const valueRon = toNumber(row.value_ron);
    return {
        key: `${row.metal_type}|${toNumber(row.fineness)}|${row.inventory_date}`,
        kind: 'inventory',
        label: row.inventory_date,
        percent: '',
        quantity,
        purifiedQuantity: toNumber(row.purified_quantity),
        avgPrice: perGram(valueRon, quantity),
        valueRon,
    };
};

/** A fineness row: the sum of its inventories, which stay as its children. */
const finenessRow = (
    metalType: string,
    fineness: number,
    inventories: StockTableRow[],
): StockTableRow => {
    const quantity = inventories.reduce((sum, inventory) => sum + inventory.quantity, 0);
    const valueRon = inventories.reduce((sum, inventory) => sum + inventory.valueRon, 0);
    return {
        key: `${metalType}|${fineness}`,
        kind: 'fineness',
        label: String(fineness),
        percent: formatPurityPercent(fineness),
        quantity,
        purifiedQuantity: inventories.reduce(
            (sum, inventory) => sum + inventory.purifiedQuantity,
            0,
        ),
        avgPrice: perGram(valueRon, quantity),
        valueRon,
        children: inventories,
    };
};

/** The inventory rows of one metal grouped by fineness, in first-seen order. */
const groupByFineness = (metalType: string, rows: StockState[]): StockTableRow[] => {
    const inventoriesByFineness = new Map<number, StockTableRow[]>();
    for (const row of rows) {
        const fineness = toNumber(row.fineness);
        inventoriesByFineness.set(fineness, [
            ...(inventoriesByFineness.get(fineness) ?? []),
            inventoryRow(row),
        ]);
    }
    return [...inventoriesByFineness].map(([fineness, inventories]) =>
        finenessRow(metalType, fineness, inventories),
    );
};

const metalTotal = (
    metalType: string,
    rows: StockState[],
    exchangeRate: number | null,
): StockMetalTotal => {
    const kind = classifyMetal(metalType);
    const finenessRows = groupByFineness(metalType, rows);
    const quantity = finenessRows.reduce((sum, row) => sum + row.quantity, 0);
    const valueRon = finenessRows.reduce((sum, row) => sum + row.valueRon, 0);
    const au585Quantity =
        kind === 'gold'
            ? rows.reduce(
                  (sum, row) => sum + toAu585(toNumber(row.quantity), toNumber(row.fineness)),
                  0,
              )
            : null;
    return {
        metalType,
        kind,
        rows: finenessRows,
        quantity,
        purifiedQuantity: finenessRows.reduce((sum, row) => sum + row.purifiedQuantity, 0),
        au585Quantity,
        avgPrice: perGram(valueRon, quantity),
        valueRon,
        valueEur: toEur(valueRon, exchangeRate),
    };
};

/**
 * The stock of each metal, gold first and the others in first-seen order: its finenesses (each
 * summed over its inventories, which are its children), and its totals with the worth in EUR at
 * `exchangeRate` (RON per EUR).
 */
export const totalStockByMetal = (
    rows: StockState[],
    exchangeRate: number | null,
): StockMetalTotal[] => {
    const rowsByMetal = new Map<string, StockState[]>();
    for (const row of rows) {
        rowsByMetal.set(row.metal_type, [...(rowsByMetal.get(row.metal_type) ?? []), row]);
    }
    const totals = [...rowsByMetal].map(([metalType, metalRows]) =>
        metalTotal(metalType, metalRows, exchangeRate),
    );
    // Array.prototype.sort is stable, so the non-gold metals keep their first-seen order.
    return totals.sort(
        (left, right) => Number(right.kind === 'gold') - Number(left.kind === 'gold'),
    );
};

/**
 * The figures above all the tables. The weights and the PMP are the gold's: AU585 and AU999.99 mean
 * nothing for silver, which has its own block. The worth is the whole stock's.
 */
export const overallStockFigures = (
    totals: StockMetalTotal[],
    exchangeRate: number | null,
): StockFigures => {
    const gold = totals.filter(total => total.kind === 'gold');
    const goldQuantity = gold.reduce((sum, total) => sum + total.quantity, 0);
    const goldValueRon = gold.reduce((sum, total) => sum + total.valueRon, 0);
    const valueRon = totals.reduce((sum, total) => sum + total.valueRon, 0);
    return {
        quantity: goldQuantity,
        purifiedQuantity: gold.reduce((sum, total) => sum + total.purifiedQuantity, 0),
        // Only gold has an AU585 figure; the others count as none.
        au585Quantity: totals.reduce((sum, total) => sum + (total.au585Quantity ?? 0), 0),
        avgPrice: perGram(goldValueRon, goldQuantity),
        valueRon,
        valueEur: toEur(valueRon, exchangeRate),
    };
};
